import {
  ENVIRONMENT,
  EVENT_LEAD_FORM_SUBMITTED,
  EVENT_PAGE_VIEWED,
  EVENT_THANK_YOU_PAGE_VIEWED,
  EVENT_TRIGGER_NON_ESSENTIAL_SCRIPTS,
  PRODUCTION,
  TIKTOK,
} from "@shared/constants";

import { logDebug, logError } from "@shared/functions/log";

import type DataLayer from "@client/classes/data-layer/data-layer";

import type Event from "@packages/types/event";
import type Events from "@packages/types/events";
import type { LeadSubmissionResponse } from "@packages/types/front-end-api/lead-submission";

type Window = typeof window & {
  ttq: { identify: Function; track: Function };
};

type TtMeta = NonNullable<
  NonNullable<LeadSubmissionResponse["ad_platform_signals"]>["tt_meta"]
>;

export const PAGE_VIEW_EVENT = "PageView";

export default class TiktokEvents {
  /**
   * Reference to the `DataLayer` class.
   */
  readonly dataLayer: typeof DataLayer;
  private hasFired = false;

  /**
   * Initialize `TiktokEvents` class
   */
  constructor(dataLayer: typeof DataLayer) {
    this.dataLayer = dataLayer;

    this.dataLayer.events.subscribe(
      EVENT_LEAD_FORM_SUBMITTED,
      this.handleLeadFormSubmitted.bind(this),
    );

    this.dataLayer.events.subscribe(
      EVENT_THANK_YOU_PAGE_VIEWED,
      this.handleThankYouPageViewed.bind(this),
    );

    logDebug({ message: "Class 'TiktokEvents' initialized" });
  }

  /**
   * Send "lead form submitted" data
   */
  private handleLeadFormSubmitted(
    _: string,
    { data }: Event.Data<Events.LeadFormSubmitted>,
  ) {
    if (this.hasFired || !data?.tt_meta) {
      return;
    }

    this.fireTtMeta(data.tt_meta);
  }

  private handleThankYouPageViewed(
    _: string,
    { data }: Event.Data<Events.ThankYouPage>,
  ) {
    const ttMeta = data?.adPlatformSignals?.tt_meta;

    if (this.hasFired || !ttMeta) {
      return;
    }

    this.fireTtMeta(ttMeta);
  }

  private fireTtMeta(ttMeta: TtMeta) {
    const events = Array.isArray(ttMeta.events)
      ? ttMeta.events.filter((event) => event?.event)
      : [];

    if (!events.length) {
      return;
    }

    this.hasFired = true;

    if (ENVIRONMENT === PRODUCTION && ttMeta.user) {
      try {
        logDebug({
          message: "Identifying user with TikTok",
          event: {
            name: "Identify",
            meta: { ttMeta },
            destination: TIKTOK,
          },
        });
        (window as Window).ttq.identify({
          email: ttMeta.user?.email,
          phone_number: ttMeta.user?.phone,
          external_id: ttMeta.user?.external_id,
        });
      } catch (error) {
        logError({
          message: "TikTok function ttq.identify() failed.",
          error,
          event: {
            name: "Identify",
            meta: { ttMeta },
            destination: TIKTOK,
          },
        });
      }
    }

    events.forEach((event: { event: string; properties?: any; event_id?: string }) => {
      this.send(event.event, event.properties, event.event_id);
    });
  }

  /**
   * Send event data to TikTok
   *
   * @param {string} event Name of event being fired
   * @param {object} data Event data to send
   * @param {string} [eventId] Optional event ID
   */
  private send(event: string, data: any = null, eventId?: string) {
    const tiktokEvent = () => {
      if (ENVIRONMENT === PRODUCTION) {
        try {
          logDebug({
            message: `Tracking event "${event}" with TikTok`,
            event: {
              name: event,
              meta: { data, event_id: eventId },
              destination: TIKTOK,
            },
          });
          (window as Window).ttq.track(event, data, { event_id: eventId });
        } catch (error) {
          logError({
            message: "TikTok function ttq.track() failed.",
            error,
            event: {
              name: event,
              meta: { data, event_id: eventId },
              destination: TIKTOK,
            },
          });
        }
      }

      this.dataLayer.events.log(event, data, TIKTOK);
    };

    if (this.dataLayer.events.nonEssentialScriptsReady) {
      tiktokEvent();
    } else {
      let fired = false;
      const onNonEssentialScripts = () => {
        if (fired) return;
        fired = true;
        // Defer unsubscribe so it doesn't mutate the handlers array while
        // PubSub.publish() is iterating it with for...of (which would shift
        // indices and cause subsequent handlers to be skipped).
        queueMicrotask(() =>
          this.dataLayer.events.unsubscribe(
            EVENT_TRIGGER_NON_ESSENTIAL_SCRIPTS,
            onNonEssentialScripts,
          )
        );
        logDebug({
          message:
            "Non-essential scripts are ready, firing deferred TikTok event",
          event: {
            name: event,
            meta: { data, event_id: eventId },
            destination: TIKTOK,
          },
        });
        tiktokEvent();
      };
      this.dataLayer.events.subscribe(
        EVENT_TRIGGER_NON_ESSENTIAL_SCRIPTS,
        onNonEssentialScripts,
      );
    }
  }
}
